import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { X, Lightning, ShieldWarning, CheckCircle, Warning } from "@phosphor-icons/react";
import { logger } from "@/lib/logger";

/**
 * Mass-Filing modal: pick states → SSE-stream Claude drafts for each State AG in parallel.
 * Props:
 *   caseId    — string
 *   open      — boolean
 *   onClose() — close handler
 *   onDone(updatedCase) — called with fresh case doc after all states complete
 */
export default function MassFileModal({ caseId, open, onClose, onDone }) {
  const [states, setStates] = useState([]); // [{code,name}]
  const [selected, setSelected] = useState(() => new Set());
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ finished: 0, total: 0, done: [], errors: [] });
  const [elapsedSecs, setElapsedSecs] = useState(0);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/state-ags").then((r) => setStates(r.data)).catch(() => toast.error("Failed to load state list"));
  }, [open]);

  useEffect(() => {
    // Reset when reopened
    if (open && !running) {
      setSelected(new Set());
      setProgress({ finished: 0, total: 0, done: [], errors: [] });
      setElapsedSecs(0);
    }
  }, [open, running]);

  const toggle = (code) => {
    if (running) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const selectAll = () => !running && setSelected(new Set(states.map((s) => s.code)));
  const selectNone = () => !running && setSelected(new Set());

  const submit = async () => {
    if (selected.size === 0) return;
    setRunning(true);
    setProgress({ finished: 0, total: selected.size, done: [], errors: [] });
    setElapsedSecs(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const token = localStorage.getItem("sf_token");
      const resp = await fetch(`${API}/cases/${caseId}/mass-file/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ states: Array.from(selected) }),
        signal: controller.signal,
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let finalCase = null;
      let errMsg = null;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const raw of parts) {
          const lines = raw.split("\n");
          let evt = "message";
          let data = "";
          for (const l of lines) {
            if (l.startsWith("event:")) evt = l.slice(6).trim();
            else if (l.startsWith("data:")) data += l.slice(5).trim();
          }
          if (!data) continue;
          try {
            const p = JSON.parse(data);
            if (evt === "progress") {
              setProgress((prev) => ({
                finished: (prev.finished || 0) + 1,
                total: p.total || prev.total,
                done: p.status === "done" ? [...prev.done, p.state] : prev.done,
                errors: p.status === "error" ? [...prev.errors, { state: p.state, error: p.error }] : prev.errors,
              }));
            } else if (evt === "heartbeat") {
              setElapsedSecs(p.elapsed_seconds || 0);
            } else if (evt === "complete") {
              finalCase = p;
            } else if (evt === "error") {
              errMsg = p.detail || "Mass-file failed";
            }
          } catch (e) { logger.error("SSE parse error", e); }
        }
      }
      if (errMsg) throw new Error(errMsg);
      if (finalCase) {
        toast.success(`Mass-file complete: ${progress.done.length + 1} drafts generated`);
        onDone?.(finalCase);
      }
    } catch (e) {
      if (e.name === "AbortError") { toast.info("Mass-file cancelled"); return; }
      toast.error(e.message || "Mass-file failed");
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  };

  const cancel = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const close = () => {
    if (running) {
      if (!confirm("Cancel the in-flight mass-file? Completed drafts are already saved.")) return;
      cancel();
    }
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="mass-file-modal">
      <div className="bg-[#0D0D0D] border border-[#1A1A1A] w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#1A1A1A]">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-[#FF3366] border border-[#FF3366]/40 px-2 py-1">
                MASS-FILING MODE
              </span>
              <span className="font-mono-sf text-[10px] uppercase tracking-wider text-neutral-500">
                // 50 STATE AGS + DC
              </span>
            </div>
            <h2 className="font-heading font-black uppercase tracking-tighter text-2xl md:text-3xl">
              Pick your <span className="text-[#FF3366]">battlefronts</span>.
            </h2>
          </div>
          <button
            onClick={close}
            className="w-9 h-9 border border-[#1A1A1A] hover:border-[#FF3366] hover:text-[#FF3366] flex items-center justify-center transition-colors"
            data-testid="mass-file-close-btn"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Cost warning */}
        <div className="px-6 py-3 border-b border-[#1A1A1A] bg-[#FF3366]/5 flex items-start gap-3">
          <ShieldWarning size={16} weight="bold" className="text-[#FF3366] mt-0.5 flex-shrink-0" />
          <p className="text-xs font-mono-sf text-neutral-300 leading-relaxed">
            <span className="text-[#FF3366] font-bold uppercase tracking-wider">Cost warning:</span> each state
            fires an independent Claude draft (~$0.02–$0.05 in AI cost each). Selecting all 51 = ~$1–2.50
            per run. Drafts are saved as they complete — cancelling keeps whatever finished.
          </p>
        </div>

        {/* Controls */}
        <div className="px-6 py-3 border-b border-[#1A1A1A] flex flex-wrap items-center gap-2">
          <button onClick={selectAll} disabled={running} className="h-8 px-3 border border-[#1A1A1A] hover:bg-[#1A1A1A] disabled:opacity-40 text-[10px] font-mono-sf uppercase tracking-[0.2em] transition-colors" data-testid="mass-file-select-all">
            Select All
          </button>
          <button onClick={selectNone} disabled={running} className="h-8 px-3 border border-[#1A1A1A] hover:bg-[#1A1A1A] disabled:opacity-40 text-[10px] font-mono-sf uppercase tracking-[0.2em] transition-colors" data-testid="mass-file-select-none">
            Clear
          </button>
          <span className="ml-auto font-mono-sf text-xs uppercase tracking-[0.2em] text-neutral-400" data-testid="mass-file-selected-count">
            {selected.size} / {states.length} selected
          </span>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="mass-file-state-grid">
            {states.map((s) => {
              const isSelected = selected.has(s.code);
              const isDone = progress.done.includes(s.code);
              const err = progress.errors.find((e) => e.state === s.code);
              return (
                <button
                  key={s.code}
                  onClick={() => toggle(s.code)}
                  disabled={running}
                  title={s.name}
                  className={`p-3 border transition-all text-left disabled:cursor-not-allowed relative ${
                    err ? "border-[#FF3366] bg-[#FF3366]/10" :
                    isDone ? "border-[#00FFCC] bg-[#00FFCC]/10" :
                    isSelected ? "border-[#FF3366] bg-[#FF3366]/20" :
                    "border-[#1A1A1A] hover:border-neutral-600"
                  }`}
                  data-testid={`mass-file-state-${s.code}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-heading font-black text-lg tracking-tight">{s.code}</span>
                    {isDone && <CheckCircle size={14} weight="fill" className="text-[#00FFCC]" />}
                    {err && <Warning size={14} weight="fill" className="text-[#FF3366]" />}
                  </div>
                  <div className="font-mono-sf text-[9px] uppercase tracking-[0.15em] text-neutral-500 truncate mt-1">
                    {s.name.replace(" Attorney General", "")}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer / progress */}
        <div className="border-t border-[#1A1A1A] p-6">
          {running ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono-sf text-sm text-[#FF3366] cursor-blink" data-testid="mass-file-progress-label">
                  claude-sonnet-4.5 :: firing {progress.total} state AGs :: {elapsedSecs}s
                </div>
                <div className="font-mono-sf text-xs uppercase tracking-[0.2em] text-neutral-400">
                  {progress.finished} / {progress.total}
                </div>
              </div>
              <div className="w-full h-2 bg-[#1A1A1A]" data-testid="mass-file-progress-bar">
                <div
                  className="h-full bg-[#FF3366] transition-all"
                  style={{ width: `${progress.total ? (progress.finished / progress.total) * 100 : 0}%` }}
                />
              </div>
              <button
                onClick={cancel}
                className="mt-4 h-9 px-4 border border-[#1A1A1A] hover:border-[#FF3366] hover:text-[#FF3366] text-[10px] font-mono-sf uppercase tracking-[0.2em] transition-colors"
                data-testid="mass-file-cancel-btn"
              >
                Cancel run
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4">
              <div className="font-mono-sf text-xs text-neutral-400 uppercase tracking-wider">
                Est. cost: <span className="text-white font-bold">${(selected.size * 0.04).toFixed(2)}</span> · Time: <span className="text-white font-bold">~{Math.max(30, Math.ceil((selected.size / 3) * 25))}s</span>
              </div>
              <button
                onClick={submit}
                disabled={selected.size === 0}
                className="h-11 px-6 bg-[#FF3366] hover:bg-[#FF5248] disabled:opacity-30 disabled:cursor-not-allowed text-white text-xs font-mono-sf uppercase tracking-[0.25em] font-bold flex items-center gap-2 transition-colors"
                data-testid="mass-file-submit-btn"
              >
                <Lightning size={14} weight="bold" />
                Fire {selected.size} draft{selected.size === 1 ? "" : "s"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
